import { QuestelServicesClient, RequestResult, HttpMethodEnum } from '@questelservices/common';
import store from '@/store';
import { BatchDetailsDto } from '@/models/batch/BatchDetailsDto';
import { BatchEventDetailsDto } from '@/models/batch/BatchEventDetailsDto';
import { ValidationResultDto } from '@/models/ValidationResultDto';
import { BatchNameValidationParameters } from '@/models/batch/BatchNameValidationParameters';
import { BatchDto } from '@/models/batch/BatchDto';
import { BatchDataDeleteDto } from '@/models/batch/BatchDataDeleteDto';
import { BatchDataDeleteRequestDto } from '@/models/batch/BatchDataDeleteRequestDto';
import { BatchEventTypeDateDto } from '@/models/batch/BatchEventTypeDateDto';
import { BatchSnapshotDto } from '@/models/batch/BatchSnapshotDto';
import { ReportsSentDateDto } from '@/models/batch/ReportsSentDateDto';
import { SendBatchReportsRequestDto } from '@/models/batch/SendBatchReportsRequestDto';
import { ClientEbillingReportDto } from '@/models/e-billing/ClientEbillingReportDto';

export class BatchService {
  private client: QuestelServicesClient;

  constructor() {
    this.client = new QuestelServicesClient(store, '');
  }

  public async getCurrentBatchId(clientId: Guid): Promise<Guid> {
    return await this.client.get<Guid>(`/api/clients/${clientId}/batches/current`);
  }

  public async getActiveBatchIds(clientId: Guid): Promise<Guid[]> {
    return await this.client.get<Guid[]>(`/api/clients/${clientId}/batches/active`);
  }

  public async getBatchEventTypeDate(clientId: Guid, param: BatchEventTypeDateDto): Promise<RequestResult> {
    const url = `/api/batchevents/${clientId}/typedate`;

    return await this.client.sendRequest<RequestResult>(HttpMethodEnum.Post, url, param);
  }

  // Gets an array containing all the batches for the given client.
  // If closed is not null and true, then return only closed batches.
  async getClientBatches(clientId: Guid, closed?: boolean): Promise<Array<BatchDetailsDto>> {
    let url = `/api/clients/${clientId}/batches`;
    if (closed != null) {
      url += `?closed=${closed}`;
    }

    return await this.client.get<Array<BatchDetailsDto>>(url);
  }

  public async getRecentClientBatches(clientId: Guid): Promise<BatchDetailsDto[]> {
    return await this.client.get<BatchDetailsDto[]>(`/api/clients/${clientId}/batches/recent`);
  }

  async getClosedClientBatches(clientId: Guid): Promise<BatchDto[]> {
    return await this.client.get<BatchDto[]>(`/api/clients/${clientId}/batches/closed`);
  }

  public async getClientBatchesReportStatus(clientId: Guid): Promise<BatchDetailsDto[]> {    
    return await this.client.get<BatchDetailsDto[]>(`/api/clients/${clientId}/batches/reportStatus`);
  }

  async getBatchDetails(batchId: Guid): Promise<BatchDetailsDto> {
    return await this.client.get<BatchDetailsDto>(`/api/batches/${batchId}`);
  }

  public async processBatchEvent(batchEventId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Patch, `/api/batchevents/${batchEventId}/processeddate`);
  }

  public async getBatchEvents(batchId: Guid): Promise<BatchEventDetailsDto[]> {
    return await this.client.get<BatchEventDetailsDto[]>(`/api/batches/${batchId}/batchevents`);
  }

  public async startAutoCoding(batchId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Post, `/api/batches/${batchId}/autocoding`);
  }

  public async startAzureCoding(batchId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Post, `/api/batches/${batchId}/azurecoding`);
  }

  public async startPostProcessing(batchId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Post, `/api/batches/${batchId}/postprocessing`);
  }

  public async addNewBatch(clientId: Guid): Promise<RequestResult> {
    return await this.client.postJson('/api/batches/', clientId);
  }

  public async completeBatch(batchId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Post, `/api/batches/${batchId}/complete`);
  }

  public async updateBatch(batchId: Guid, data: BatchDto): Promise<BatchDto> {
    return await this.client.getData<BatchDto>(HttpMethodEnum.Put, `/api/batches/${batchId}`, data);
  }

  public async setReadyForReporting(clientId: Guid): Promise<void> {
    await this.client.sendRequest(HttpMethodEnum.Put, `/api/batches/${clientId}/sendreport`);
  }

  public async validateBatchName(parameters: BatchNameValidationParameters): Promise<ValidationResultDto> {
    return await this.client.getPostData<ValidationResultDto>('/api/batches/validation', parameters);
  }

  public async getBatchDataDeleteTree(batchId: Guid): Promise<BatchDataDeleteDto> {
    return await this.client.get<BatchDataDeleteDto>(`/api/batches/batch-data-delete-tree/${batchId}`);
  }

  public async deleteBatchData(dataToDelete: BatchDataDeleteRequestDto) {
    return await this.client.sendRequest(HttpMethodEnum.Delete, '/api/batches/delete-batch-data', dataToDelete);
  }
  
  async sendReport(batchId: Guid, options?: SendBatchReportsRequestDto): Promise<ReportsSentDateDto> {
    return await this.client.getPostData<ReportsSentDateDto>(`/api/batches/${batchId}/send-reports`, options);
  }

  async getEbillingReports(batchId: Guid): Promise<ClientEbillingReportDto[]> {
    let reports = await this.client.get<ClientEbillingReportDto[]>(`/api/batches/${batchId}/e-billing-reports`);
    reports.forEach((r, i) => {
      r.ClientEbillingReportKey = i + 1;
    });

    return reports;
  }

  // Get a list of batch snapshots for the given batch.
  async getBatchSnapshots(batchId: Guid): Promise<BatchSnapshotDto[]> {
    let batchSnapshots = await this.client.get<BatchSnapshotDto[]>(`/api/batches/${batchId}/batch-snapshots`)
    return batchSnapshots;
  }

  // Start the snapshot process for the given batch.
  async snapshotBatch(batchId: Guid): Promise<RequestResult> {
    return await this.client.sendRequest(HttpMethodEnum.Post, `/api/batches/${batchId}/snapshot-batch`);  
  }
}
